/**
 * Cookie Consent Banner — GDPR/CCPA-aware.
 *
 * Shows a fixed bottom banner on first visit. User can Accept All, Reject
 * Non-Essential, or Customize per category. Choices persist in localStorage
 * under `ftl_consent_v1` with the cookie-policy version pinned so re-consent
 * is triggered when the policy materially changes.
 *
 * Design choices:
 * - Strictly-necessary cookies are always on (session, CSRF, consent itself).
 * - Functional and analytics default OFF until explicit accept.
 * - GPC signal is treated as a "reject non-essential" by default.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { m, AnimatePresence } from "framer-motion";
import { X, Settings } from "lucide-react";
import { apiClient, getAccessToken } from "@/api/client";
import { bannerDeferredOn } from "./cookieBannerRoutes";

const STORAGE_KEY = "ftl_consent_v1";

/**
 * Best-effort: mirror a SIGNED-IN user's cookie choices to a server-side audit
 * record (GDPR/ePrivacy evidence of what an identified subject consented to).
 * localStorage stays the UI source of truth; anonymous visitors and any server
 * failure fall back to localStorage-only. Never throws / never blocks the banner.
 */
function persistConsentToServer(payload: ConsentChoices): void {
  if (!getAccessToken()) return; // anonymous → nothing to attribute a record to
  void apiClient
    .post("/legal/cookie-consent", {
      functional: payload.functional,
      analytics: payload.analytics,
      policy_version: payload.policy_version,
    })
    .catch(() => {
      /* localStorage remains authoritative; ignore server errors */
    });
}

export interface ConsentChoices {
  /** Always true — included for completeness. */
  strictly_necessary: true;
  functional: boolean;
  analytics: boolean;
  /** Cookie-policy version these choices were recorded against. */
  policy_version: string;
  /** ISO timestamp the user made the choice. */
  decided_at: string;
  /**
   * True when this choice was set automatically from a Global Privacy Control
   * (GPC) signal rather than an explicit banner interaction (CCPA/CPRA opt-out
   * preference signal). Recorded for audit — a GPC-driven reject is still a
   * legally valid opt-out.
   */
  gpc?: boolean;
}

const POLICY_VERSION = "v0.1-draft";

/**
 * Global Privacy Control — the browser/extension sets `navigator.globalPrivacyControl`
 * (and sends the `Sec-GPC: 1` header) to signal a "do not sell/share / reject
 * non-essential" preference. Under CCPA/CPRA this is a legally binding opt-out we
 * must honor without a banner interaction. Feature-detected; absent on browsers
 * that don't implement it.
 */
function gpcEnabled(): boolean {
  return (
    typeof navigator !== "undefined" &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  );
}

function readStoredConsent(): ConsentChoices | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentChoices;
    if (parsed.policy_version !== POLICY_VERSION) {
      // Policy version changed → require fresh consent.
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(
  choices: Omit<ConsentChoices, "policy_version" | "decided_at" | "strictly_necessary" | "gpc">,
  gpc = false,
) {
  const payload: ConsentChoices = {
    strictly_necessary: true,
    functional: choices.functional,
    analytics: choices.analytics,
    policy_version: POLICY_VERSION,
    decided_at: new Date().toISOString(),
    ...(gpc ? { gpc: true } : {}),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  persistConsentToServer(payload);
  return payload;
}

/**
 * Resolve the consent to start from. A previously stored decision always wins.
 * Otherwise, if the browser signals GPC, we honor it immediately — record a
 * reject-non-essential choice (and mirror it to the server audit) so the banner
 * never appears and analytics/functional cookies stay off. Returns null only
 * when there's no stored choice and no GPC signal (→ show the banner).
 */
function resolveInitialConsent(): ConsentChoices | null {
  const stored = readStoredConsent();
  if (stored) return stored;
  if (gpcEnabled()) {
    return writeConsent({ functional: false, analytics: false }, true);
  }
  return null;
}

/** Read the user's current consent. Returns null if not yet decided. */
export function useConsent(): ConsentChoices | null {
  const [consent, setConsent] = useState<ConsentChoices | null>(() => resolveInitialConsent());

  useEffect(() => {
    const onStorage = () => setConsent(readStoredConsent());
    window.addEventListener("storage", onStorage);
    window.addEventListener("ftl-consent-change", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ftl-consent-change", onStorage);
    };
  }, []);

  return consent;
}

/**
 * CSS custom property carrying the banner's live height.
 *
 * The banner is `position: fixed; bottom-0` and therefore OUT OF FLOW — it
 * reserves no space, so it sat on top of whatever was at the bottom of the
 * page. On a 360-375px phone it covers roughly the bottom 270px (~40% of the
 * viewport), and because it is fixed, scrolling does not free what is beneath.
 *
 * That put it over the primary controls of every unauthenticated page:
 *   /login            password field, show-password toggle, "Forgot password?",
 *                     "Sign in", "Sign in with Google"
 *   /register         password + confirm, "Create account", "Sign up with Google"
 *   /forgot-password  "Send reset link"
 *
 * A first-time visitor on a phone is exactly the person who sees this banner —
 * it only shows before a choice is stored — so the people it blocked were new
 * users trying to sign up.
 *
 * Height is measured rather than hard-coded because it is not constant: the
 * copy wraps differently by width, and "Customize" expands the panel. The
 * layout consumes it via `padding-bottom` (see index.css).
 */
const BANNER_HEIGHT_VAR = "--ftl-cookie-banner-height";

function setBannerHeight(px: number): void {
  document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${px}px`);
}

// Row db174b50: the banner waits on the guest join and meeting pages
// (cookieBannerRoutes.ts says why that is consent-safe).
export function CookieConsentBanner() {
  const { pathname } = useLocation();
  const deferred = bannerDeferredOn(pathname);
  const [choice, setChoice] = useState<ConsentChoices | null>(() => resolveInitialConsent());
  const [showCustomize, setShowCustomize] = useState(false);
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  // Publish the live height so the page below can reserve space for it. A ref
  // callback plus ResizeObserver rather than a one-shot measure: the height
  // changes when "Customize" expands and when the copy re-wraps on rotate.
  const measureRef = useCallback((node: HTMLDivElement | null) => {
    bannerRef.current = node;
    setBannerHeight(node ? node.getBoundingClientRect().height : 0);
  }, []);

  useEffect(() => {
    const node = bannerRef.current;
    if (!node || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      // BORDER box, not content box.
      //
      // This read `entry.contentRect.height`, which EXCLUDES padding — and this
      // element carries `pb-4 sm:pb-6`. So the observer published a height 16px
      // (24px at sm) SHORTER than the banner really is, and because it fires
      // after the ref callback above, the wrong number won: measured at 390px,
      // the variable said 171px while the banner's own rect was 187px.
      //
      // Everything that reserves space from this variable was short by exactly
      // that padding — the page's padding-bottom, and the mobile nav's offset —
      // so the bottom strip of the banner still sat on top of whatever was
      // under it. Two ways of measuring the same box, disagreeing, with the
      // wrong one last.
      //
      // getBoundingClientRect() matches what the ref callback already does, so
      // the two paths now agree by construction.
      setBannerHeight(node.getBoundingClientRect().height);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [choice, showCustomize]);

  // Reclaim the space the moment the banner goes — on decision, and on unmount
  // so a route change cannot strand the padding.
  useEffect(() => {
    if (choice) setBannerHeight(0);
    return () => setBannerHeight(0);
  }, [choice]);

  // Don't render if user has already decided, or on a meeting route (above).
  // Unmounting runs measureRef(null), which zeroes the reserved height.
  if (choice || deferred) return null;

  const commit = (next: Omit<ConsentChoices, "policy_version" | "decided_at" | "strictly_necessary">) => {
    const saved = writeConsent(next);
    setChoice(saved);
    window.dispatchEvent(new Event("ftl-consent-change"));
  };

  return (
    <AnimatePresence>
      <m.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        role="dialog"
        aria-label="Cookie consent"
        ref={measureRef}
        className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6"
      >
        <div className="max-w-4xl mx-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-3 sm:p-5">
          {!showCustomize ? (
            <div className="flex flex-col sm:flex-row items-start gap-2 sm:gap-4">
              {/* Explicit dismiss button — implicit dismiss records
                  "reject non-essential" so the banner doesn't reappear
                  on every page nav. Pre-fix users who navigated away
                  without clicking saw the banner persistently across
                  sessions because no consent was ever written. */}
              <button
                onClick={() => commit({ functional: false, analytics: false })}
                aria-label="Dismiss cookie banner (rejects non-essential cookies)"
                className="absolute top-3 right-3 p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="flex-1 pr-8">
                <p className="text-sm font-semibold text-text-primary mb-1">
                  We use cookies
                </p>
                {/* Compact on phones. The banner is `fixed bottom-0`, so every
                    line of copy is a line of the sign-up form it covers: at
                    360x640 the full text made it 269px — 42% of the viewport —
                    and the password field and "Sign in" sat underneath it. The
                    detail is not lost, it is one tap away in the Cookie Policy,
                    which is what that link is for, and all three choices are
                    unchanged. */}
                <p className="text-xs text-text-secondary leading-relaxed sm:hidden">
                  Essential cookies keep you signed in. Optional ones are off by
                  default. See our{" "}
                  <Link to="/legal/cookies" className="text-brand-700 dark:text-brand-300 underline">
                    Cookie Policy
                  </Link>
                  .
                </p>
                <p className="hidden sm:block text-xs text-text-secondary leading-relaxed">
                  Strictly-necessary cookies keep you signed in and protect against CSRF.
                  Functional and analytics cookies are optional and off by default.
                  See our{" "}
                  <Link to="/legal/cookies" className="text-brand-700 dark:text-brand-300 underline">
                    Cookie Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/legal/privacy" className="text-brand-700 dark:text-brand-300 underline">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  onClick={() => setShowCustomize(true)}
                  className="text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-text-primary hover:bg-slate-50 dark:hover:bg-slate-800/40 flex items-center gap-1.5"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Customize
                </button>
                <button
                  onClick={() => commit({ functional: false, analytics: false })}
                  className="text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-text-primary hover:bg-slate-50 dark:hover:bg-slate-800/40"
                >
                  Reject non-essential
                </button>
                <button
                  onClick={() => commit({ functional: true, analytics: true })}
                  className="text-xs px-3 py-2 rounded-lg bg-brand-500 text-white hover:bg-brand-600 font-medium"
                >
                  Accept all
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm font-semibold text-text-primary">Cookie preferences</p>
                <button
                  onClick={() => setShowCustomize(false)}
                  aria-label="Close preferences"
                  className="p-1 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded"
                >
                  <X className="h-4 w-4 text-text-secondary" />
                </button>
              </div>
              <div className="space-y-3">
                <CategoryRow
                  title="Strictly necessary"
                  description="Required for login, security, and recording your consent choice."
                  checked={true}
                  disabled
                  onChange={() => {}}
                />
                <CategoryRow
                  title="Functional"
                  description="Remember theme, dashboard layout, and language preference."
                  checked={functional}
                  onChange={setFunctional}
                />
                <CategoryRow
                  title="Analytics"
                  description="Aggregate usage so we can improve the product. No third-party ads."
                  checked={analytics}
                  onChange={setAnalytics}
                />
              </div>
              <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => commit({ functional: false, analytics: false })}
                  className="text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-text-primary hover:bg-slate-50 dark:hover:bg-slate-800/40"
                >
                  Reject non-essential
                </button>
                <button
                  onClick={() => commit({ functional, analytics })}
                  className="text-xs px-3 py-2 rounded-lg bg-brand-500 text-white hover:bg-brand-600 font-medium"
                >
                  Save preferences
                </button>
              </div>
            </div>
          )}
        </div>
      </m.div>
    </AnimatePresence>
  );
}

function CategoryRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className={`flex items-start gap-3 rounded-lg p-3 border border-slate-200 dark:border-slate-700 ${disabled ? "opacity-60" : "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5"
      />
      <div className="flex-1">
        <p className="text-sm font-medium text-text-primary">{title}</p>
        <p className="text-xs text-text-secondary mt-0.5">{description}</p>
      </div>
    </label>
  );
}
